import { FeatureURI, AdvertiserType } from '@sbt-web/network/types';
import { evalBoolFeature } from '../features/evalBoolFeature.js';
import '../features/formatMainData.js';

/**
 * Determines if an ad item is shippable based on its features and advertiser type.
 *
 * - For non-Pro advertisers, the item is shippable if the `ItemShippable` feature is enabled.
 * - For Pro advertisers, the item is shippable only if the `ItemShippable` feature is enabled
 *   and the advertiser has `proTransactionsEnabled` set to `true`.
 *
 * @param ad - The ad item to check for shippability.
 * @returns `true` if the item is shippable, otherwise `false`.
 *
 * @example
 * ```ts
 * // Example AdItem for a non-Pro advertiser with shippable feature enabled
 * const ad1 = {
 *   advertiser: { type: AdvertiserType.Individual },
 *   features: { [FeatureURI.ItemShippable]: true }
 * } as AdItem;
 * isShippable(ad1); // true
 *
 * // Example AdItem for a Pro advertiser with shippable feature enabled and transactions enabled
 * const ad2 = {
 *   advertiser: { type: AdvertiserType.Pro, proTransactionsEnabled: true },
 *   features: { [FeatureURI.ItemShippable]: true }
 * } as AdItem;
 * isShippable(ad2); // true
 *
 * // Example AdItem for a Pro advertiser with shippable feature enabled but transactions disabled
 * const ad3 = {
 *   advertiser: { type: AdvertiserType.Pro, proTransactionsEnabled: false },
 *   features: { [FeatureURI.ItemShippable]: true }
 * } as AdItem;
 * isShippable(ad3); // false
 *
 * // Example AdItem for a non-Pro advertiser with shippable feature disabled
 * const ad4 = {
 *   advertiser: { type: AdvertiserType.Individual },
 *   features: { [FeatureURI.ItemShippable]: false }
 * } as AdItem;
 * isShippable(ad4); // false
 * ```
 */
const isShippable = (ad) => {
    const advertiser = ad.advertiser;
    const isAdShippable = evalBoolFeature(ad.features[FeatureURI.ItemShippable]);
    const isPro = advertiser.type === AdvertiserType.Pro;
    const isAProWithTransactions = isPro && !!advertiser.proTransactionsEnabled;
    return isAdShippable && (!isPro || isAProWithTransactions);
};

export { isShippable };
//# sourceMappingURL=isShippableItem.js.map
